/**
 * Highlighting utilities for SEC filing viewer
 *
 * These functions find and highlight the Nth occurrence of search text
 * within an HTML document, similar to Ctrl+F functionality.
 */

/**
 * Find and highlight the Nth instance of search text
 * Returns true if the instance was found and highlighted
 */
export function findAndHighlightInstance(
  container: HTMLElement,
  searchText: string,
  instance: number
): boolean {
  // Clear any existing highlights first
  clearHighlights(container);

  if (!searchText || instance < 1) return false;

  // Walk through all text nodes and find matches
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  const matches: { node: Text; startOffset: number; endOffset: number }[] = [];
  const searchLower = searchText.toLowerCase();

  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent || '';
    const textLower = text.toLowerCase();

    let startIndex = 0;
    let matchIndex: number;

    while ((matchIndex = textLower.indexOf(searchLower, startIndex)) !== -1) {
      matches.push({
        node,
        startOffset: matchIndex,
        endOffset: matchIndex + searchText.length,
      });
      startIndex = matchIndex + 1;
    }
  }

  if (matches.length === 0) return false;

  // Highlight all matches, with special styling for the target instance
  const targetIndex = Math.min(instance - 1, matches.length - 1);

  // We need to process matches in reverse order to avoid offset issues
  // when modifying the DOM
  const sortedMatches = [...matches].reverse();

  sortedMatches.forEach((match, reversedIndex) => {
    const actualIndex = matches.length - 1 - reversedIndex;
    const isTarget = actualIndex === targetIndex;

    highlightTextNode(match.node, match.startOffset, match.endOffset, isTarget);
  });

  // Scroll to the target match (instant, no animation)
  const targetElement = container.querySelector('.sec-highlight-active');
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'instant',
      block: 'center',
    });
  }

  return true;
}

/**
 * Highlight a portion of a text node
 */
function highlightTextNode(
  textNode: Text,
  startOffset: number,
  endOffset: number,
  isActive: boolean
): void {
  const text = textNode.textContent || '';
  if (startOffset >= text.length) return;

  // Create the highlight span
  const span = document.createElement('span');
  span.className = isActive
    ? 'sec-highlight sec-highlight-active'
    : 'sec-highlight';
  span.textContent = text.slice(startOffset, endOffset);

  // Split the text node and insert the highlight
  const before = text.slice(0, startOffset);
  const after = text.slice(endOffset);

  const parent = textNode.parentNode;
  if (!parent) return;

  // Create new text nodes for before and after
  const beforeNode = document.createTextNode(before);
  const afterNode = document.createTextNode(after);

  // Replace the original text node with the three parts
  parent.insertBefore(beforeNode, textNode);
  parent.insertBefore(span, textNode);
  parent.insertBefore(afterNode, textNode);
  parent.removeChild(textNode);
}

/**
 * Clear all highlights from the container
 */
export function clearHighlights(container: HTMLElement): void {
  const highlights = container.querySelectorAll('.sec-highlight');
  highlights.forEach(highlight => {
    const parent = highlight.parentNode;
    if (parent) {
      // Replace the highlight span with its text content
      const textNode = document.createTextNode(highlight.textContent || '');
      parent.replaceChild(textNode, highlight);
      // Normalize to merge adjacent text nodes
      parent.normalize();
    }
  });
}
